import { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, MailCheck } from "lucide-react";
import AuthPage, { inputClass, labelClass, primaryBtn } from "@/components/account/AuthPage";
import { api, formatApiError } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { useSeo } from "@/lib/seo";

export default function Register() {
  const { t, lang } = useLang();
  useSeo({ title: `${t("auth.registerTitle")} – Nivå Digital`, description: t("auth.registerSub"), noindex: true });
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/register", { ...form, lang });
      setDone(true);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthPage tag={t("nav.account")} title={t("auth.registerTitle")} sub={t("auth.registerSub")} testid="register-page">
      {done ? (
        <div data-testid="register-success" className="flex flex-col items-center py-6 text-center">
          <MailCheck className="h-12 w-12 text-mint" />
          <h2 className="mt-5 font-display text-xl font-bold text-bone">{t("auth.checkInbox")}</h2>
          <p className="mt-3 text-sm leading-relaxed text-fog">{t("auth.checkInboxText").replace("{email}", form.email)}</p>
          <Link data-testid="register-to-login" to="/logga-in" className="mt-6 text-sm font-semibold text-mint hover:underline">
            {t("auth.loginLink")}
          </Link>
        </div>
      ) : (
        <form data-testid="register-form" onSubmit={submit} className="space-y-5">
          <div>
            <label htmlFor="reg-name" className={labelClass}>{t("auth.name")}</label>
            <input id="reg-name" data-testid="register-name" required value={form.name} onChange={update("name")} className={inputClass} autoComplete="name" />
          </div>
          <div>
            <label htmlFor="reg-email" className={labelClass}>{t("auth.email")}</label>
            <input id="reg-email" data-testid="register-email" type="email" required value={form.email} onChange={update("email")} className={inputClass} autoComplete="email" />
          </div>
          <div>
            <label htmlFor="reg-password" className={labelClass}>{t("auth.password")}</label>
            <input id="reg-password" data-testid="register-password" type="password" required minLength={8} value={form.password} onChange={update("password")} className={inputClass} autoComplete="new-password" />
            <p className="mt-1.5 text-xs text-fog/70">{t("auth.passwordHint")}</p>
          </div>
          {error && (
            <p data-testid="register-error" className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-400">{error}</p>
          )}
          <button data-testid="register-submit" type="submit" disabled={loading} className={primaryBtn}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : t("auth.registerBtn")}
          </button>
          <p className="text-center text-sm text-fog">
            {t("auth.hasAccount")}{" "}
            <Link data-testid="register-login-link" to="/logga-in" className="font-semibold text-mint hover:underline">{t("auth.loginLink")}</Link>
          </p>
        </form>
      )}
    </AuthPage>
  );
}
